/**
 * Closed set of audit actions (DATA-MODEL.md §4, SEC-1, SEC-11). Adding one requires updating
 * `shared/audit/__tests__/actions.test.ts`. Format `<entity>.<verb>`.
 */
export const AUDIT_ACTIONS = [
  // authentication (platform table)
  "auth.sign_in",
  "auth.sign_in_failed",
  "auth.sign_out",
  "auth.token_rejected",
  "auth.denied",
  "auth.reauth",
  // Admin MFA (2026-09-29): a person remembered this browser after a sign-in that proved a second
  // factor, or forgot it. Identifiers and dates only, never the cookie's value.
  "auth.mfa_device_remember",
  "auth.mfa_device_forget",
  // access
  "access.grant",
  "access.revoke",
  "access.reactivate",
  // Round 7: a new invitation email for a person pending their first sign-in (no address).
  "access.invite_resend",
  "user.create",
  "user.activate",
  "user.deactivate",
  // S9: a person moved to another identity provider (single or per user in a bulk rebind)
  "user.rebind",
  // #1639 C1 (ADR 0006 amendment 2026-10-21): the runner closed a request to create a person's
  // "Sign in with email" account (created, exists, not_configured, failed, refused). Ids and
  // closed codes only, never the address, an object id or a Graph answer (SEC-17).
  "account.create",
  // Group access (2026-09-28): a directory group mapped to a role and removed; a person enrolled
  // through a group and ended (left the group, mapping removed, stale); an override set on one
  // group-enrolled person; a workforce PlatformAdmin reaching an enforcing tenant (break-glass);
  // the tenant's "Require a directory group" switch. Identifiers only (SEC-17).
  "group_mapping.create",
  "group_mapping.remove",
  "access.group_grant",
  "access.group_end",
  "access.group_override_set",
  "access.group_enforcement_exempt",
  "tenant.group_enforcement_update",
  // tenancy. `customer.create` and `customer.update` are history: no route writes them since
  // round 6 T-04 folded customers into tenants (platform 0027), but stored rows keep them, and
  // the trail's filter and event schema must still read them.
  "customer.create",
  "customer.update",
  "tenant.create",
  "tenant.update",
  // T-51 (round 8): which connectors the tenant offers for new connections, written by its
  // TenantAdmin or, from Tenants, by a PlatformAdmin (who may lock it).
  "tenant.connector_access_update",
  "tenant.connector_access_platform_update",
  // #1111: the tenant's Allowed notification domains (on or off, and the list), by its TenantAdmin.
  "tenant.notification_domains_update",
  // Round 10 (Explorer guard, lock 2): a PlatformAdmin's tenant switch for the warehouse catalog.
  // Historical since ADR 0011 (2026-10-08): the Explorer and the switch are removed.
  "tenant.warehouse_catalog_update",
  // S8: the runner's tenant_db job (a tenant database was created or adopted)
  "tenant.provision",
  // ADR 0010: a PlatformAdmin's Retry of a failed tenant resources job (a route), and the
  // provisioner's own rows: the tenant's Azure resources applied, and a grant the pairing check
  // revoked (a `suspend` revokes nothing since F32: disabling is an app-level block). Names and
  // ids only.
  "tenant.resources_retry",
  // F31: a PlatformAdmin's Apply now, a new apply job for a ready or failed tenant (a route).
  "tenant.resources_apply",
  // F32 PR3: a PlatformAdmin deleted a disabled tenant; the metadata holds its offboarding list
  // (resource ids and names, secret names, never a value).
  "tenant.delete",
  // #826: a PlatformAdmin configured or removed a canary tenant's daily canary (instance,
  // schedule, limits and how many tables per connection; ids and counts, no table name).
  "canary.configure",
  "canary.remove",
  "tenant.resources_provision",
  "tenant.resources_grant_revoked",
  // The hourly orphan scan, once per resource: an orphan reported for a person (a vault, an
  // account, or an identity it could not retire), and an orphan identity it deleted.
  "tenant.resources_orphan_found",
  "tenant.resources_orphan_deleted",
  // An unexpected grant the sweep may not delete (above a tenant resource), reported once.
  "tenant.resources_grant_found",
  // A name of the tenant's resources was in use, so the provisioner raised the generation
  // (generation_from, generation_to, reason "name_in_use"; codes only, never the taken name).
  "tenant.resources_generation_raised",
  "instance.create",
  "instance.update",
  "instance.provision",
  "instance.disable",
  "instance.enable",
  // F36: a PlatformAdmin's Retry of an instance's failed provisioning job (a route).
  "instance.provisioning_retry",
  "instance.mark_storage_removed",
  // S8: the converter moved an instance into its tenant's database (or rolled it back)
  "instance.convert",
  "binding.read",
  "binding.update",
  // S10: Connect read a dbt repository's refs, tree and project file (counts only)
  "binding.probe",
  // #923: the destination's Test or Check now (mode, code and pass only)
  "binding.test",
  // connectors
  "connector.seed",
  "connector.update",
  "connector.version.add",
  // T-51: a connector switched on or off for new connections in every tenant.
  "connector.availability_update",
  // Round 10 wave 2 (V6): the runner removed a wave 1 catalog-only definition no connection uses.
  "connector.remove_catalog_only",
  // dataflows
  "dataflow.create",
  "dataflow.update",
  "dataflow.delete",
  "dataflow.read",
  "dataflow.validate",
  "dataflow.revalidate",
  "dataflow.state_change",
  // 2026-09-28: a connection turned off and on again by hand (the reason is on the row)
  "dataflow.disable",
  "dataflow.enable",
  // 2026-09-29 (product gaps row 7): the schema change policy, and a decision on logged changes
  "dataflow.schema_policy.update",
  "dataflow.schema_change.decide",
  "dataflow.entities.update",
  // Round 7: the runner's maintenance pass made the connections of a connector that gained
  // incremental support incremental, after moving them into their schedules' and runs' full sets
  // (`runner/maintenance/refreshTypes.ts`). Counts, ids and connector names only.
  "dataflow.refresh_type.derive",
  "hub.browse",
  // secrets
  "secret.write",
  "secret.read",
  "secret.delete",
  // 2026-09-28 cred-check: a date typed for an expiring credential (field, day, source; no value)
  "credential.expiry.update",
  // schedules
  "schedule.create",
  "schedule.update",
  "schedule.delete",
  "schedule.toggle",
  "schedule.pause",
  "schedule.resume",
  "schedule.skip",
  // runs
  "run.start",
  "run.fire",
  "run.retry",
  "run.retry_tables",
  "run.cancel",
  "run.force_reset",
  "run.technical_details_view",
  "run.finalize",
  // Round 7: the runner's run retention pass removed runs older than the instance's retention
  // days (`runner/maintenance/runRetention.ts`). Counts and the cut-off only, one row per pass.
  "run.retention",
  "step.transition",
  // dbt jobs (round 6 T-29 renamed them from `transformation.*`; the old four below are history)
  "dbt_job.create",
  "dbt_job.update",
  "dbt_job.delete",
  // Session 11: the audited read of a dbt node's compiled and executed SQL (ids and bytes only).
  "dbt_job.sql.read",
  // History: rows written before round 6 T-29 keep these; no route writes them now.
  "transformation.create",
  "transformation.update",
  "transformation.delete",
  "transformation.sql.read",
  // settings and platform
  "settings.update",
  "migration.apply",
  // Notifications: a rules save, a test send (instance or email), and the L1 (first-line) email
  // list. Counts only, never an address. The L1 action is spelled without a digit because every
  // action segment is lower-case letters and underscores (see the format test).
  "notification.rules.update",
  "notification.test",
  "notification.first_line.update",
  // Alert routing: one person's Watch on one connection or schedule, on or off. Ids, the target
  // type and the outcomes only, never an address; the actor is the person (no one watches for
  // someone else).
  "notification.watch.update",
  // Security alerts: one TenantAdmin's or PlatformAdmin's own subscription, the categories kept
  // on. Never an address; the actor is the person (no one subscribes someone else).
  "notification.security.update",
  // #463: an operator released parked emails (`scripts/sql/release-parked-emails.sql`, run as the
  // migrator, never a route). The delivery ids, their count and the change record only, never an
  // address.
  "notification.release",
  // Display time zone (round 3, 2026-09-30): a person's own zone, chosen or written once from the
  // browser (`metadata.reason: "default"`). The zone names only; the actor is the person.
  "user.preferences.update",
  // Last instance (round 4, 2026-09-30): the instance a person switched to, stored so their next
  // sign-in lands there. The instance ids before and after only; the same id again writes no row.
  "user.last_instance.update",
  // R1 (round 10 wave 2): a token renamed a person between sign-ins (Entra is the source of truth
  // for a workforce name). Ids only; never the name, before or after.
  "user.display_name_refresh",
  // skyMonitor (spec section 9d): a check switched, re-limited, muted or reset; an incident
  // taken ("Assign to me") or closed as "Not an issue" (which mutes its check for 7 days). Ids,
  // switches and counts only; a table name stays in the instance schema.
  "monitor.check.update",
  "monitor.incident.acknowledge",
  "monitor.incident.dismiss",
  // Round 7 (T-31): the Explorer's Refresh, a request for a warehouse catalog snapshot the runner
  // took (ADR 0008). The request id only. Historical since ADR 0011 (2026-10-08).
  "explorer.refresh",
  // MCP (ADR 0009, round 8). The OAuth endpoints write `mcp.client_registered`,
  // `mcp.grant_issued` and `mcp.refresh_reuse` themselves; `mcp.consent_approve`,
  // `mcp.consent_deny`, `mcp.grant_revoked`, `mcp.revoke_all`, `mcp.write_approve`,
  // `mcp.write_declined` and `mcp.switch_update` are
  // `/api/v2` mutations in `server/audit/registry.ts`; every read tool call writes one `mcp.read`
  // and every write tool call one `mcp.write_call` (release 8B: prepared, committed, waiting for
  // approval or refused; the write itself is its route's own row, `source: "mcp"`).
  // Identifiers, the tool, the client and the grant only; never a value (SEC-17).
  "mcp.client_registered",
  "mcp.consent_approve",
  "mcp.consent_deny",
  "mcp.grant_issued",
  "mcp.grant_revoked",
  "mcp.revoke_all",
  "mcp.refresh_reuse",
  "mcp.read",
  "mcp.write_call",
  "mcp.write_approve",
  "mcp.write_declined",
  "mcp.switch_update",
  // audit self-events (SEC-12, SEC-16)
  "audit.export",
  "audit.review",
  "audit.verify",
  "audit.retention",
  "history.export",
] as const;

export type AuditAction = (typeof AUDIT_ACTIONS)[number];

/**
 * Actions stored rows still carry but no route writes any more: the trail reads them, its
 * filter offers them as words with "(history)" (`client/src/components/audit/actionLabels.ts`).
 */
export const HISTORICAL_AUDIT_ACTIONS = [
  "customer.create",
  "customer.update",
  "transformation.create",
  "transformation.update",
  "transformation.delete",
  "transformation.sql.read",
  // ADR 0011 (2026-10-08): the Explorer, its Refresh and the tenant's catalog switch are removed.
  "explorer.refresh",
  "tenant.warehouse_catalog_update",
] as const satisfies readonly AuditAction[];

export const ACTOR_TYPES = ["user", "runner", "job", "system", "uam", "migration", "seed"] as const;
export type ActorType = (typeof ACTOR_TYPES)[number];

/**
 * `cli`: an operator tool run from the allowlisted VM (the S8 instance converter). `mcp`: a call
 * made through an MCP grant (ADR 0009 section 9), taken from `req.auth.via`.
 */
export const AUDIT_SOURCES = ["api", "runner", "migration", "seed", "cli", "mcp"] as const;
export type AuditSource = (typeof AUDIT_SOURCES)[number];

export function isAuditAction(value: string): value is AuditAction {
  return (AUDIT_ACTIONS as readonly string[]).includes(value);
}
